import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { PriceHistory } from "@bargain-radar/shared";
import {
  alertsQueryOptions,
  dealQueryOptions,
  deletePriceAlert,
  priceHistoryQueryOptions,
  requestPurchaseAdvice,
  sendAdviceFeedback,
  setPriceAlert,
} from "../queries/deals";
import { formatPrice, relativeTime } from "../lib/format";
import { DealPrice } from "../components/deal-card";
import { VoteButtons } from "../components/vote-buttons";
import { CommentThread } from "../components/comment-thread";

export const Route = createFileRoute("/deal/$id/$slug")({
  loader: ({ context, params }) => {
    void context.queryClient.prefetchQuery(dealQueryOptions(params.id));
  },
  component: DealDetail,
});

function DealDetail() {
  const { id } = Route.useParams();
  const router = useRouter();
  const viewerId = router.options.context.session?.user.id ?? null;
  const q = useQuery(dealQueryOptions(id));
  const history = useQuery(priceHistoryQueryOptions(id));
  const alerts = useQuery({ ...alertsQueryOptions, enabled: Boolean(viewerId) });
  const [targetPrice, setTargetPrice] = useState("");
  const advice = useMutation({ mutationFn: () => requestPurchaseAdvice(id) });
  const feedback = useMutation({ mutationFn: (helpful: boolean) => sendAdviceFeedback(id, q.data!.priceCents!, helpful) });
  const alert = alerts.data?.find((item) => item.dealId === id);
  const saveAlert = useMutation({
    mutationFn: () => setPriceAlert(id, Math.round(Number(targetPrice) * 100)),
    onSuccess: () => alerts.refetch(),
  });
  const removeAlert = useMutation({ mutationFn: () => deletePriceAlert(id), onSuccess: () => alerts.refetch() });

  if (q.isPending) {
    return <p className="mx-auto max-w-3xl px-5 py-8">Loading…</p>;
  }
  if (q.isError || !q.data) {
    return <p className="mx-auto max-w-3xl px-5 py-8 text-[color:var(--color-red)]">Deal not found.</p>;
  }
  const d = q.data;

  return (
    <main className="mx-auto w-full max-w-3xl px-4 sm:px-5 py-6 sm:py-8 flex-1">
      <p className="text-xs text-[color:var(--color-fg-muted)] mb-2">
        <Link to="/" className="hover:underline">← back to hot</Link>
      </p>
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight leading-snug">
        {d.title}
      </h1>
      <p className="mt-1 text-sm text-[color:var(--color-fg-muted)]">
        {d.merchant && <span>{d.merchant}</span>}
        {d.category && (
          <>
            {d.merchant && <span> · </span>}
            <Link to="/category/$slug" params={{ slug: d.category.slug }} className="hover:underline">
              {d.category.name}
            </Link>
          </>
        )}
        <span> · posted {relativeTime(d.createdAt)}</span>
        {d.postedBy?.name && <span> · by {d.postedBy.name}</span>}
      </p>

      <section className="mt-5 grid sm:grid-cols-[160px_1fr] gap-5">
        {d.imageUrl ? (
          <img
            src={d.imageUrl}
            alt=""
            className="rounded-lg w-full sm:w-40 sm:h-40 object-cover bg-[color:var(--color-panel-2)]"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="rounded-lg w-full sm:w-40 sm:h-40 grid place-items-center bg-[color:var(--color-panel-2)] text-[color:var(--color-fg-muted)] text-xs">
            no image
          </div>
        )}
        <div>
          <DealPrice deal={d} prominent />
          <p className="mt-3">
            <a
              href={d.url}
              target="_blank"
              rel="noopener nofollow ugc"
              className="inline-flex items-center gap-2 rounded-md bg-[color:var(--color-accent)] text-[color:var(--color-bg)] px-4 py-2 font-medium hover:opacity-90"
            >
              Go to deal →
            </a>
          </p>
          <div className="mt-4 flex items-center gap-4 text-sm text-[color:var(--color-fg-muted)]">
            <VoteButtons
              kind="deal"
              targetId={d.id}
              score={d.voteScore}
              myVote={d.myVote}
              invalidate={["deal", d.id]}
            />
            <span>💬 {d.commentCount}</span>
            {d.tags.length > 0 && (
              <span className="flex flex-wrap gap-1">
                {d.tags.map((t) => (
                  <Link
                    key={t.slug}
                    to="/tag/$slug"
                    params={{ slug: t.slug }}
                    className="rounded-full border border-[color:var(--color-line)] px-2 py-0.5 hover:bg-[color:var(--color-panel-2)]"
                  >
                    #{t.slug}
                  </Link>
                ))}
              </span>
            )}
          </div>
        </div>
      </section>

      {d.description && (
        <section className="mt-6 prose prose-invert max-w-none">
          <p className="whitespace-pre-wrap leading-relaxed">{d.description}</p>
        </section>
      )}

      <section className="mt-6 rounded-lg border border-[color:var(--color-line)] bg-[color:var(--color-panel)] p-4">
        <h2 className="font-semibold">Price history</h2>
        {history.data?.points.length ? <PriceChart history={history.data} currency={d.currency} /> : (
          <p className="mt-2 text-sm text-[color:var(--color-fg-muted)]">No recorded prices yet.</p>
        )}
        <div className="mt-4 border-t border-[color:var(--color-line)] pt-4">
          {viewerId ? <>
            <label className="text-sm font-medium" htmlFor="target-price">Alert me at or below</label>
            <div className="mt-2 flex gap-2">
              <input id="target-price" type="number" min="0.01" step="0.01" value={targetPrice} onChange={(e) => setTargetPrice(e.target.value)} placeholder={alert ? String(alert.targetPriceCents / 100) : "e.g. 99.00"} className="min-w-0 flex-1 rounded-md border border-[color:var(--color-line)] bg-[color:var(--color-bg)] px-3 py-2" />
              <button type="button" disabled={!Number.isFinite(Number(targetPrice)) || Number(targetPrice) <= 0 || saveAlert.isPending} onClick={() => saveAlert.mutate()} className="rounded-md bg-[color:var(--color-accent)] px-3 py-2 text-sm font-medium text-[color:var(--color-bg)] disabled:opacity-50">{alert ? "Update" : "Set alert"}</button>
              {alert && <button type="button" onClick={() => removeAlert.mutate()} className="px-2 text-sm text-[color:var(--color-fg-muted)]">Remove</button>}
            </div>
            {alert && <p className="mt-2 text-xs text-[color:var(--color-fg-muted)]">Watching {formatPrice(alert.targetPriceCents, d.currency)}{alert.triggeredAt ? " · target reached" : ""}</p>}
          </> : <Link to="/login" className="text-sm text-[color:var(--color-accent)] hover:underline">Sign in to set a price alert</Link>}
        </div>
      </section>

      <section className="mt-6 rounded-lg border border-[color:var(--color-line)] bg-[color:var(--color-panel)] p-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="font-semibold">Ask Jev: should I buy this?</h2>
            <p className="mt-1 text-sm text-[color:var(--color-fg-muted)]">
              Jev weighs the product against its recorded price history.
            </p>
          </div>
          {viewerId ? (
            <button
              type="button"
              onClick={() => advice.mutate()}
              disabled={advice.isPending}
              className="shrink-0 rounded-md bg-[color:var(--color-accent)] px-3 py-2 text-sm font-medium text-[color:var(--color-bg)] disabled:opacity-50"
            >
              {advice.isPending ? "Asking…" : "Ask Jev"}
            </button>
          ) : (
            <Link to="/login" className="shrink-0 text-sm text-[color:var(--color-accent)] hover:underline">
              Sign in to ask
            </Link>
          )}
        </div>
        {advice.data && (
          <div className="mt-4 border-t border-[color:var(--color-line)] pt-4">
            <p className="text-lg font-semibold">
              {{ buy: "Buy", wait: "Wait", skip: "Don't buy", insufficient_data: "Not enough data" }[advice.data.verdict]}
              {advice.data.confidence != null && (
                <span className="ml-2 text-sm font-normal text-[color:var(--color-fg-muted)]">
                  {Math.round(advice.data.confidence * 100)}% confidence
                </span>
              )}
            </p>
            <p className="mt-1 text-sm">{advice.data.reason}</p>
            <p className="mt-1 text-xs text-[color:var(--color-fg-muted)]">
              Based on {advice.data.sampleCount} recorded prices
              {advice.data.lowestPriceCents != null && advice.data.medianPriceCents != null && (
                <> · low {formatPrice(advice.data.lowestPriceCents, d.currency)} · median {formatPrice(advice.data.medianPriceCents, d.currency)}</>
              )}
            </p>
            {advice.data.verdict !== "insufficient_data" && <div className="mt-3 text-xs text-[color:var(--color-fg-muted)]">
              Helpful? <button type="button" onClick={() => feedback.mutate(true)} className="ml-2 hover:text-[color:var(--color-fg)]">👍 Yes</button><button type="button" onClick={() => feedback.mutate(false)} className="ml-3 hover:text-[color:var(--color-fg)]">👎 No</button>
            </div>}
          </div>
        )}
        {advice.isError && (
          <p className="mt-3 text-sm text-[color:var(--color-red)]">Jev is unavailable. Please try again.</p>
        )}
        <p className="mt-3 text-xs text-[color:var(--color-fg-muted)]">AI guidance based on recorded prices, not financial advice.</p>
      </section>

      <CommentThread dealId={d.id} currentUserId={viewerId} />
    </main>
  );
}

function PriceChart({ history, currency }: { history: PriceHistory; currency: string }) {
  const values = history.points.map((point) => point.priceCents);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = Math.max(1, max - min);
  const points = history.points.map((point, index) => {
    const x = history.points.length === 1 ? 300 : (index / (history.points.length - 1)) * 600;
    const y = 150 - ((point.priceCents - min) / range) * 120;
    return `${x},${y}`;
  }).join(" ");
  return <div className="mt-3">
    <svg viewBox="0 0 600 180" role="img" aria-label={`Price history from ${formatPrice(min, currency)} to ${formatPrice(max, currency)}`} className="h-44 w-full overflow-visible">
      <line x1="0" y1="150" x2="600" y2="150" stroke="var(--color-line)" />
      <polyline points={points} fill="none" stroke="var(--color-accent)" strokeWidth="4" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
    <div className="flex justify-between text-xs text-[color:var(--color-fg-muted)]">
      <span>Low {formatPrice(history.lowestPriceCents, currency)}</span>
      <span>Median {formatPrice(history.medianPriceCents, currency)}</span>
      <span>High {formatPrice(history.highestPriceCents, currency)}</span>
    </div>
  </div>;
}
